import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const scopes = [
  {
    en: 'Strategy',
    jp: 'ブランド戦略・広報支援',
    icon: 'ri-lightbulb-line',
    desc: '制作物の前段階として、何をどう伝えるかを設計します。戦略なき制作は、届かない制作です。',
    items: [
      '意味設計・ブランドコンセプト開発',
      'コピーライティング・編集',
      'メディア戦略・広報計画',
      'ステークホルダーコミュニケーション設計',
      '内部研修・発信体制構築',
    ],
    featured: true,
  },
  {
    en: 'Design & Ad',
    jp: '広告・デザイン制作',
    icon: 'ri-palette-line',
    desc: 'ブランドの意味を、視覚言語に変換します。見た目だけでなく、伝わる理由のあるデザインを。',
    items: [
      'ロゴ・VI（ビジュアルアイデンティティ）',
      'パンフレット・リーフレット・冊子',
      'ポスター・バナー・広告物',
      '広報誌・アニュアルレポート',
      'パッケージ・サイン・展示物',
    ],
    featured: false,
  },
  {
    en: 'Web & Digital',
    jp: 'Web・デジタル制作',
    icon: 'ri-layout-2-line',
    desc: 'コンテンツ設計から実装まで。組織の価値が伝わるWebサイトを、戦略から一貫して制作します。',
    items: [
      'コーポレートサイト・採用サイト',
      'ランディングページ（LP）',
      'コンテンツ設計・情報設計',
      'SNS・メールマガジン設計',
      'アクセス解析・改善支援',
    ],
    featured: false,
  },
  {
    en: 'Video & Film',
    jp: '映像・動画制作',
    icon: 'ri-film-line',
    desc: '組織の現場と人を、動く言葉で伝えます。インタビュー映像から広告動画まで幅広く対応。',
    items: [
      '組織紹介・ブランドムービー',
      'インタビュー・ドキュメンタリー',
      'SNS向けショート動画',
      'イベント・記録映像',
      'アニメーション・モーショングラフィック',
    ],
    featured: false,
  },
];

export default function ScopeSection() {
  const heading = useScrollAnimation();
  const strategy = useScrollAnimation();
  const cards = useStaggerAnimation(scopes.length - 1);
  const link = useScrollAnimation();

  const [featuredScope, ...restScopes] = scopes;

  return (
    <section className="texture-scope py-28 md:py-40 bg-warm-light relative overflow-hidden">

      {/* ── 「制作領域・グリッド・モジュール展開」をテーマにした装飾 ── */}

      {/* 上部中央の「モジュール展開」— 4つの領域が広がる */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none hidden lg:block">
        {/* 4分割を示す十字線 */}
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-amber/25 to-transparent" style={{ left: -30, top: 0 }} />
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-gold/20 to-transparent" style={{ left: -10, top: 0 }} />
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-amber/20 to-transparent" style={{ left: 10, top: 0 }} />
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-gold/25 to-transparent" style={{ left: 30, top: 0 }} />
        {/* 上端の接続点 */}
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-amber/35" style={{ left: -31, top: -1 }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-gold/30" style={{ left: -11, top: -1 }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-amber/28" style={{ left: 9, top: -1 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-gold/35" style={{ left: 29, top: -1 }} />
      </div>

      {/* 左上の「整然とした格子」— 制作の構造性 */}
      <div className="absolute -top-8 -left-8 pointer-events-none">
        <div className="absolute rounded-full border border-accent-gold/16" style={{ width: 160, height: 160, top: 0, left: 0 }} />
        <div className="absolute rounded-full border border-accent-amber/10 hidden sm:block" style={{ width: 280, height: 280, top: -60, left: -60 }} />
        <div className="absolute rounded-full border border-accent-gold/7 hidden md:block" style={{ width: 400, height: 400, top: -120, left: -120 }} />
      </div>

      {/* 右下のドットグリッド（大）— 展開・広がり */}
      <div className="absolute bottom-0 right-0 w-40 h-32 lg:w-80 lg:h-64 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.22) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
      </div>

      {/* 中央上の「4領域を示す菱形」 */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 pointer-events-none">
        <div className="w-8 h-8 lg:w-14 lg:h-14 border-2 border-accent-amber/35 rotate-45" />
        <div className="absolute border border-accent-gold/42 rotate-45" style={{ width: 16, height: 16, left: 8, top: 8 }} />
        <div className="absolute rounded-full bg-accent-amber/40" style={{ width: 5, height: 5, left: 12, top: 12 }} />
      </div>

      {/* 右上の「モジュール格子」— 制作物の整然とした配置 */}
      <div className="absolute top-16 right-16 pointer-events-none hidden lg:block">
        {[0, 1, 2].map(row => (
          <div key={row} className="flex gap-3 mb-3">
            {[0, 1, 2, 3].map(col => (
              <div
                key={col}
                className="border"
                style={{
                  width: 10, height: 10,
                  borderColor: `rgba(${(row + col) % 3 === 0 ? '212,122,46' : (row + col) % 3 === 1 ? '201,168,76' : '74,158,142'},${0.30 - (row + col) * 0.02})`,
                  transform: 'rotate(45deg)',
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* 浮遊ドット（4色・4領域に対応） */}
      <div className="absolute top-[30%] right-[15%] w-5 h-5 rounded-full bg-accent-amber/28 pointer-events-none" />
      <div className="absolute bottom-[30%] left-[20%] w-4 h-4 rounded-full bg-accent-gold/32 pointer-events-none" />
      <div className="absolute top-[60%] right-[35%] w-3 h-3 bg-accent-teal/22 rotate-45 pointer-events-none" />
      <div className="absolute top-[45%] left-[12%] w-3 h-3 rounded-full bg-accent-amber/25 pointer-events-none hidden lg:block" />

      {/* 左中央の「展開を示す放射線」 */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 pointer-events-none hidden xl:block">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className="absolute w-px"
            style={{
              height: 30 + i * 8,
              background: `rgba(${i % 2 === 0 ? '212,122,46' : '201,168,76'},${0.22 - i * 0.03})`,
              left: i * 8,
              top: -(15 + i * 4),
              transform: `rotate(${-15 + i * 10}deg)`,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

        {/* Heading */}
        <div
          ref={heading.ref as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 transition-all duration-700"
          style={{ opacity: heading.isVisible ? 1 : 0, transform: heading.isVisible ? 'translateY(0)' : 'translateY(28px)' }}
        >
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">What We Make</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
              つくれるもの、<br />できること。
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
              広告・グラフィック・Web・映像の制作から、<br />
              ブランド戦略・広報支援まで。<br />
              部分的なご依頼も、全体設計も対応します。
            </p>
          </div>
        </div>

        {/* Strategy — Featured Card (full width, dark) */}
        <div
          ref={strategy.ref as React.RefObject<HTMLDivElement>}
          className="mb-px transition-all duration-700"
          style={{ opacity: strategy.isVisible ? 1 : 0, transform: strategy.isVisible ? 'translateY(0)' : 'translateY(32px)' }}
        >
          <div className="bg-forest-green border border-forest-green p-12 md:p-16 flex flex-col md:flex-row gap-12 md:gap-20">
            {/* Left */}
            <div className="flex-shrink-0 md:w-72">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center border border-warm-white/20 flex-shrink-0">
                  <i className={`${featuredScope.icon} text-accent-teal text-base`} />
                </div>
                <div>
                  <span className="font-garamond text-2xl font-light text-warm-white block">{featuredScope.en}</span>
                  <span className="font-serif-jp text-xs font-light text-warm-white/50 tracking-normal-jp">/ {featuredScope.jp}</span>
                </div>
              </div>
              <p className="font-sans-jp text-sm text-warm-white/60 leading-relaxed-jp">{featuredScope.desc}</p>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px bg-warm-white/10 self-stretch" />

            {/* Right — items */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5 content-center">
              {featuredScope.items.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="block w-1 h-1 rounded-full bg-accent-teal flex-shrink-0 mt-2" />
                  <span className="font-sans-jp text-sm text-warm-white/80 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Other 3 cards */}
        <div
          ref={cards.ref as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-divider border border-t-0 border-divider"
        >
          {restScopes.map((s, i) => (
            <div
              key={s.en}
              className="bg-warm-light p-10 md:p-12 flex flex-col gap-6 transition-all duration-700 hover:bg-warm-white"
              style={{
                opacity: cards.visibleCount > i ? 1 : 0,
                transform: cards.visibleCount > i ? 'translateY(0)' : 'translateY(28px)',
                transitionDelay: `${i * 100}ms`,
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-divider flex-shrink-0">
                  <i className={`${s.icon} text-accent-teal text-base`} />
                </div>
                <div>
                  <span className="font-garamond text-xl font-light text-charcoal block">{s.en}</span>
                  <span className="font-serif-jp text-xs font-light text-muted-brown tracking-normal-jp">/ {s.jp}</span>
                </div>
              </div>
              <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{s.desc}</p>
              <div className="space-y-2.5 mt-auto pt-6 border-t border-divider">
                {s.items.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="block w-1 h-1 rounded-full bg-accent-teal flex-shrink-0 mt-2" />
                    <span className="font-sans-jp text-xs text-charcoal leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Link */}
        <div
          ref={link.ref as React.RefObject<HTMLDivElement>}
          className="mt-10 text-right transition-all duration-700"
          style={{ opacity: link.isVisible ? 1 : 0, transform: link.isVisible ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <Link
            to="/dekirukoto"
            className="inline-flex items-center gap-2 text-sm font-sans-jp text-muted-brown hover:text-accent-teal transition-colors duration-200 whitespace-nowrap group cursor-pointer"
          >
            制作・支援の詳細を見る
            <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
